<template>
  <z-form ref="form" class="z-card border-0" @submit.native.prevent="handler_submit">
    <div class="row align-items-center">
      <div class="col-6">
        <div class="z-card">
          <h4>{{ $t('verification.settings.postback.title') }}</h4>

          <div class="z-card__body">
            <z-form-group class="row">
              <div class="col-3 text-right">
                {{ $t('verification.settings.postback.url.title') }}
              </div>
              <z-form-input class="col-9" :rules="{ url: true }">
                <i-input
                  v-model="form.url"
                  :placeholder="$t('verification.settings.postback.url.placeholder')"
                />
              </z-form-input>
            </z-form-group>
            <z-form-group class="row mb-0">
              <div class="col-3"></div>
              <div class="col-9 filter__item filter__item--button text-right">
                <i-button html-type="submit" type="primary" style="margin-right: 1px">
                  {{ $t('verification.settings.postback.url.save') }}
                </i-button>
              </div>
            </z-form-group>
          </div>
        </div>

        <div class="z-card mt-4">
          <h4>{{ $t('verification.settings.check.title') }}</h4>

          <div class="z-card__body">
            <verification-type-select ref="template_select" v-model="type" @input="set_template" />

            <z-form-group class="row">
              <div class="col-3 text-right">
                {{ $t('verification.settings.check.phone.title') }}
              </div>
              <z-form-input class="col-6" :rules="{ required: true, phone: true, max: 15 }">
                <i-input
                  v-model="form.phone"
                  :placeholder="$t('verification.settings.check.phone.placeholder')"
                />
              </z-form-input>
              <div class="col-3 filter__item filter__item--button text-right">
                <i-button html-type="submit" type="primary" style="margin-right: 1px">
                  {{ $t('verification.settings.check.phone.button') }}
                </i-button>
              </div>
            </z-form-group>

            <z-form-group class="row">
              <div class="col-3 text-right">
                {{ $t('verification.settings.check.code.title') }}
              </div>
              <z-form-input class="col-4" :rules="{ latin_num: true }">
                <i-input
                  v-model="form.code"
                  :placeholder="$t('verification.settings.check.code.placeholder')"
                />
              </z-form-input>
            </z-form-group>
          </div>
        </div>
      </div>
    </div>
  </z-form>
</template>

<script>
  import VerificationTypeSelect from '@verification/components/verification-type-select';
  import cloneDeep from 'lodash-es/cloneDeep';
  import { MailingListService } from '@mailer/services/MailingListService';
  import { PageServerValidationResponseMixin } from '@shared/mixins/page-server-validation-response';
  import { RecipientModalService } from '@mailer/modals/RecipientModalService';
  import { TemplateModalService } from '@mailer/modals/TemplateModalService';
  import { toBackendDateWithTime } from '@shared/helpers/date_format';
  import { validate } from 'vee-validate';
  import moment from 'moment-es6';

  export default {
    components: { VerificationTypeSelect },
    mixins: [PageServerValidationResponseMixin],
    data: () => ({
      current_sender: null,
      calculated_mailing_cost: {
        count: 0,
        cost: 0
      },
      is_loading_cost: false,
      is_delay_send: false,
      form: {
        url: '',
        name: '',
        phone: '',
        code: '',
        type: '',
        body: '',
        recipients: {
          start_at: null,
          recipients: [],
          include: [],
          exclude: []
        }
      }
    }),
    computed: {
      sender_name() {
        try {
          return this.current_sender.name;
        } catch (e) {
          return '';
        }
      }
    },

    watch: {
      form: {
        handler() {
          this.handler_change_form();
        },
        deep: true
      }
    },

    methods: {
      get_current_date() {
        this.form.start_at = moment()
          .utc()
          .format('YYYY-MM-DD hh:mm:ss');
      },
      handler_change_form() {
        this.validate_for_loading_cost()
          .then(() => {
            this.calculate_cost();
          })
          .catch(() => {});
      },
      validate_for_loading_cost() {
        return new Promise(async (resolve, reject) => {
          try {
            const recipient_validate = await validate(this.form.recipients, 'required_recipient');
            const is_recipients = recipient_validate.valid;
            const { sender: is_sender, name: is_name, body: is_body } = this.form;
            const is_validate = is_recipients && is_sender && is_name && is_body;
            if (is_validate) {
              resolve(is_validate);
            } else {
              reject({ message: 'not valid' });
            }
          } catch (e) {
            reject(e);
          }
        });
      },
      calculate_cost() {
        this.is_loading_cost = true;
        MailingListService.getCost(this.get_params())
          .then(result => {
            if (result) {
              this.calculated_mailing_cost.cost = result.cost || 0;
              this.calculated_mailing_cost.count = result.recipients || 0;
            }
          })
          .catch(() => {
            this.calculated_mailing_cost.cost = 0;
            this.calculated_mailing_cost.count = 0;
          })
          .finally(() => {
            this.is_loading_cost = false;
          });
      },
      get_params() {
        const { sender, body, name, recipients: rules, start_at } = this.form;
        const params = { sender, body, name, rules };
        if (start_at) {
          params.start_at = toBackendDateWithTime(start_at);
        }
        return params;
      },
      handler_submit() {
        this.$refs.form.validate().then(is_valid => {
          if (is_valid) {
            const params = this.get_params();

            if (!this.is_delay_send) {
              params.start_at = '';
            }

            MailingListService.createItem(params)
              .then(() => {
                this.$router.push({ name: 'mailer.mailing.index' });
              })
              .catch(error => {
                this.check_server_validation(error);
              });
          }
        });
      },

      set_template({ body }) {
        if (body) {
          this.form.body = body;
        }
      },

      save_template() {
        TemplateModalService.create({
          body: this.form.body
        }).on('success', () => this.$refs.template_select.load());
      },

      on_sender_select(value) {
        this.current_sender = value;
        if (value && value.name) {
          this.form.sender = value.name;
        } else {
          this.form.sender = value;
        }
      },

      add_recipients() {
        RecipientModalService.select({
          value: cloneDeep(this.form.recipients)
        }).on('success', res => this.$set(this.form, 'recipients', res));
      }
    }
  };
</script>

<style lang="scss">
  .save-template {
    position: absolute;
    margin-top: $spacer/4;
    font-size: 12px;
  }

  .mailing-description {
    margin-top: $spacer * 2;
    width: 380px;
    text-align: right;
    min-height: 125px;
    position: relative;
    margin-left: auto;
    margin-right: auto;
    &__error {
      color: red;
      position: absolute;
      right: 0;
    }
    h6 {
      font-size: 16px;
      margin-bottom: 10px;
    }

    h5 {
      font-size: 20px;
    }
  }
</style>
